import {useDispatch, useSelector} from "react-redux";
import {MessageSquare, Phone} from "lucide-react";
import {RootState} from "../../store/store";
import {navigateDialer, setCallFrom, setCallState, setDialerHidden} from "../../store/features/calls/dialerSlice.ts";
import {selectTenant, selectUserId} from "../../store/features/Auth/authSlice.ts";
import {makeMatrixCall} from "../../services/matrixCallService.ts";
import {useOpenDirectChat} from "../../hooks/useOpenDirectChat";
import {useIsChatEnabledQuery} from "../../services/chatService";
import {clsxMerge} from "../../utilis/classNameUtils.ts";
import {fullName, TeamMember} from "./teamFilters";
import type {User} from "../../store/features/Users/userService";
import {blockCallDuringGroupCall} from "../../services/groupCallBusyGuard.ts";

const isReachable = (member: TeamMember): boolean => {
    const status = member.telcoUserStatus?.status;
    return Boolean(status) && status !== 'OFFLINE' && status !== 'UNKNOWN';
};

/** Message + Call buttons shared by the grid card and the list row. */
const TeamMemberActions = ({member}: {member: TeamMember}) => {
    const dispatch = useDispatch();
    const tenantId = useSelector(selectTenant);
    const currentUserId = useSelector(selectUserId);
    const callState = useSelector((state: RootState) => state.dialer.callState);
    const openDirectChat = useOpenDirectChat();
    const {data: isChatEnabled} = useIsChatEnabledQuery();

    const name = fullName(member);
    const canCall = isReachable(member);

    const handleCall = async () => {
        if (blockCallDuringGroupCall(callState, dispatch)) return;
        if (!canCall || callState !== 'IDLE') return;

        if (member.doNotDisturbUserStatus?.enabled) {
            dispatch(navigateDialer(`/dnd-call`));
            dispatch(setDialerHidden(false));
            dispatch(setCallFrom(member));
            return;
        }

        dispatch(setCallState('CONNECTING'));
        dispatch(setDialerHidden(false));
        dispatch(setCallFrom(member));
        // makeMatrixCall only reads userId / firstName / lastName, which a team member carries,
        // and resolves the peer by user id to reuse the existing DM.
        await makeMatrixCall(member as unknown as User, currentUserId, tenantId, dispatch);
    };

    return (
        <div className="flex items-center gap-2 shrink-0">
            {isChatEnabled !== false && (
                <button
                    type="button"
                    data-testid="team-message"
                    aria-label={`Message ${name}`}
                    title="Message"
                    onClick={() => openDirectChat(member.userId)}
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-surface-muted text-ink-secondary hover:bg-fill-subtle"
                >
                    <MessageSquare size={16} className="!p-0 !text-ink-secondary"/>
                </button>
            )}
            <button
                type="button"
                data-testid="team-call"
                aria-label={`Call ${name}`}
                title={canCall ? 'Call' : 'User is offline'}
                disabled={!canCall}
                onClick={handleCall}
                className={clsxMerge(
                    'flex h-9 w-9 items-center justify-center rounded-full',
                    canCall
                        ? 'bg-brand-tint text-brand-ink hover:bg-brand-tint'
                        : 'bg-surface-muted text-ink-disabled cursor-not-allowed',
                )}
            >
                <Phone size={16} className={clsxMerge('!p-0', canCall ? '!text-brand-ink' : '!text-ink-disabled')}/>
            </button>
        </div>
    );
};

export default TeamMemberActions;
